// Organigramme — « Vue focus » (option D of the design prototype's
// Organigramme.html, file org-focus.jsx). One person at the centre:
// managerial chain on top, peers beside, direct reports below, the
// whole annuaire on the left rail, ↑↓←→ to walk the tree.
//
// Deviations from the prototype (deliberate):
// - the proto's shell (its own sidebar/topbar/entity pills/theme
//   toggle) is dropped — the app shell + EntityBar own those,
// - the rail groups by ENTITY (real data, real brand colours) instead
//   of the proto's demo "départements": the fiche's `department` field
//   is the job title (« Fonction » in the UI), so it feeds the role
//   line under each name,
// - « postes à pourvoir » (vacant nodes) don't exist here: a node IS an
//   employee fiche. Recruitment isn't modelled by this app,
// - re-parenting is a select + a picker modal rather than the proto's
//   drag-and-drop (that belonged to options A/B/C, and the tree has to
//   be built from scratch on first use — bulk attach is faster).
import { useEffect, useMemo, useState } from 'react';
import { Mail, Phone, Users, UserPlus, X, Search } from 'lucide-react';
import type { EntityDTO, OrgNodeDTO } from '../../api/hooks.js';
import {
  OrgAvatar, entColor, entName, entShort, orgChain, orgDesc, orgFirstName,
  orgIndex, orgMatches, softColor,
} from './common.js';

interface Props {
  nodes: OrgNodeDTO[];
  entities: Map<string, EntityDTO>;
  focusId: string;
  onFocus: (id: string) => void;
  query: string;
  onQuery: (q: string) => void;
  /** 'all' or an entity id — dims the others, never hides them (a tree
   *  filtered by entity would lose its cross-entity branches). */
  entFilter: string;
  canEdit: boolean;
  busy: boolean;
  onSetManager: (id: string, managerId: string | null) => void;
  onAttach: (ids: string[], managerId: string) => void;
  onOpenFiche: (id: string) => void;
}

export function FocusView(props: Props) {
  const { nodes, entities, focusId, onFocus, query, onQuery, entFilter, canEdit, busy } = props;
  const [orphansOnly, setOrphansOnly] = useState(false);
  const [adding, setAdding] = useState(false);
  // Phones can't afford a permanent rail — it becomes an overlay.
  const [railOpen, setRailOpen] = useState(false);

  const { byId, byParent } = useMemo(() => orgIndex(nodes), [nodes]);
  const cur = byId.get(focusId) ?? nodes[0]!;
  const chain = useMemo(() => orgChain(cur.id, byId), [cur.id, byId]);
  const kids = (byParent.get(cur.id) ?? []).map((id) => byId.get(id)!);
  const manager = cur.managerId ? byId.get(cur.managerId) ?? null : null;
  const peers = manager
    ? (byParent.get(manager.id) ?? []).filter((id) => id !== cur.id).map((id) => byId.get(id)!)
    : [];
  const team = useMemo(() => orgDesc(cur.id, byParent).size, [cur.id, byParent]);
  const attached = nodes.filter((n) => n.managerId).length;
  const orphans = nodes.length - attached;

  const go = (id: string) => { if (byId.has(id)) { onFocus(id); setRailOpen(false); } };
  const dim = (n: OrgNodeDTO) => entFilter !== 'all' && n.entityId !== entFilter;

  // ↑ manager · ↓ first report · ←→ cycle through peers.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowUp' && manager) { e.preventDefault(); go(manager.id); }
      if (e.key === 'ArrowDown' && kids.length) { e.preventDefault(); go(kids[0]!.id); }
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && manager) {
        const sibs = byParent.get(manager.id) ?? [];
        if (sibs.length < 2) return;
        e.preventDefault();
        const i = sibs.indexOf(cur.id);
        go(sibs[(i + (e.key === 'ArrowRight' ? 1 : sibs.length - 1)) % sibs.length]!);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // Annuaire (left rail): grouped by entity, filtered by the search box.
  const roster = useMemo(() => {
    const pool = nodes
      .filter((n) => orgMatches(n, query))
      .filter((n) => (orphansOnly ? !n.managerId : true));
    const groups = new Map<string, OrgNodeDTO[]>();
    for (const n of pool) {
      const list = groups.get(n.entityId);
      if (list) list.push(n);
      else groups.set(n.entityId, [n]);
    }
    const order = [...entities.keys()];
    return [...groups.entries()]
      .sort((a, b) => {
        const ia = order.indexOf(a[0]); const ib = order.indexOf(b[0]);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      })
      .map(([entityId, list]) => ({ entityId, list }));
  }, [nodes, query, orphansOnly, entities]);

  const MiniCard = ({ n, up }: { n: OrgNodeDTO; up?: boolean }) => {
    const color = entColor(entities, n.entityId);
    const sub = orgDesc(n.id, byParent).size;
    return (
      <button
        type="button"
        className={'fv-mini' + (up ? ' up' : '') + (dim(n) ? ' dim' : '')}
        style={{ ['--dc' as string]: color }}
        onClick={() => go(n.id)}
        data-testid={`org-mini-${n.id}`}
      >
        <OrgAvatar n={n} s={34} color={color} />
        <span className="fv-mini-t">
          <b>{n.name}</b>
          <small>{n.role ?? 'Fonction non renseignée'}</small>
        </span>
        {sub > 0 && (
          <span className="fv-mini-c"><Users size={11} aria-hidden="true" />{sub}</span>
        )}
      </button>
    );
  };

  const heroColor = entColor(entities, cur.entityId);

  return (
    <div className="fv" data-rail={railOpen ? 'open' : 'closed'}>
      <button
        type="button"
        className="fv-rail-toggle"
        onClick={() => setRailOpen((v) => !v)}
        data-testid="org-rail-toggle"
      >
        {railOpen ? <X size={14} aria-hidden="true" /> : <Search size={14} aria-hidden="true" />}
        Annuaire <b>{nodes.length}</b>
      </button>
      <aside className="fv-rail">
        <div className="fv-rail-h">
          Annuaire<span>{nodes.length}</span>
        </div>
        <div className="fv-rail-search">
          <Search size={14} aria-hidden="true" />
          <input
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Rechercher une personne…"
            aria-label="Rechercher une personne dans l'organigramme"
            data-testid="org-search"
          />
        </div>
        {orphans > 0 && (
          <button
            type="button"
            className={'fv-rail-filter' + (orphansOnly ? ' on' : '')}
            onClick={() => setOrphansOnly((v) => !v)}
            data-testid="org-orphans-toggle"
          >
            {orphans} non rattaché{orphans > 1 ? 's' : ''}
          </button>
        )}
        <div className="fv-rail-sc">
          {roster.map((g) => {
            const color = entColor(entities, g.entityId);
            return (
              <div className="fv-rail-g" key={g.entityId} style={{ ['--dc' as string]: color }}>
                <div className="fv-rail-sec">
                  <i /><span className="nm">{entName(entities, g.entityId)}</span>
                  <span className="n">{g.list.length}</span>
                </div>
                {g.list.map((n) => (
                  <button
                    type="button"
                    key={n.id}
                    className={'fv-rail-i' + (n.id === cur.id ? ' on' : '')}
                    onClick={() => go(n.id)}
                    data-testid={`org-rail-${n.id}`}
                  >
                    <OrgAvatar n={n} s={24} color={color} />
                    <span>
                      <b>{n.name}</b>
                      <small>{n.role ?? 'Fonction non renseignée'}</small>
                    </span>
                  </button>
                ))}
              </div>
            );
          })}
          {roster.length === 0 && <p className="fv-rail-empty">Aucun résultat.</p>}
        </div>
      </aside>

      <div className="fv-main" key={cur.id}>
        {attached === 0 && (
          <div className="fv-bootstrap">
            <b>L'organigramme n'est pas encore construit.</b>
            {canEdit
              ? ' Choisissez la personne au sommet, puis « Ajouter des rapports directs » — de proche en proche.'
              : ' Un administrateur doit rattacher chaque personne à son manager.'}
          </div>
        )}

        <div className="fv-chain">
          {chain.map((n) => (
            <span key={n.id} className="fv-crumb-w">
              <button type="button" className="fv-crumb" onClick={() => go(n.id)}>
                <OrgAvatar n={n} s={22} color={entColor(entities, n.entityId)} />
                {n.name}
              </button>
              <span className="fv-arrow" aria-hidden="true">›</span>
            </span>
          ))}
          <span className="fv-crumb cur">
            <OrgAvatar n={cur} s={22} color={heroColor} />
            {cur.name}
          </span>
        </div>

        <div className="fv-spine">
          {(manager || canEdit) && (
            <>
              <div className="fv-lvl">
                <span className="fv-lvl-l">Manager</span>
                <div className="fv-lvl-c">
                  {manager
                    ? <MiniCard n={manager} up />
                    : <div className="fv-rootnote">Sommet de l'organigramme — aucun manager.</div>}
                  {canEdit && (
                    <ManagerPicker
                      node={cur}
                      nodes={nodes}
                      byParent={byParent}
                      busy={busy}
                      onSetManager={props.onSetManager}
                    />
                  )}
                </div>
              </div>
              <div className="fv-stem" />
            </>
          )}

          <div className="fv-lvl">
            <span className="fv-lvl-l on">Focus</span>
            <div className="fv-lvl-c">
              <div className="fv-hero" style={{ ['--dc' as string]: heroColor }} data-testid="org-hero">
                <OrgAvatar n={cur} s={62} color={heroColor} />
                <div className="fv-hero-id">
                  <h2>{cur.name}</h2>
                  <p>{cur.role ?? 'Fonction non renseignée'}</p>
                  <div className="fv-hero-pills">
                    <span
                      className="org-pill"
                      style={{ background: softColor(heroColor, 13), color: heroColor }}
                      title={entName(entities, cur.entityId)}
                    >
                      {entShort(entities, cur.entityId)}
                    </span>
                    <span className="org-pill mono">{cur.code}</span>
                    {team > 0 && (
                      <span className="org-pill t">
                        <Users size={11} aria-hidden="true" />
                        {team} dans son équipe
                      </span>
                    )}
                  </div>
                </div>
                <div className="fv-hero-r">
                  {cur.email && (
                    <a className="fv-ct" href={`mailto:${cur.email}`}>
                      <Mail size={13} aria-hidden="true" />{cur.email}
                    </a>
                  )}
                  {cur.phone && (
                    <span className="fv-ct"><Phone size={13} aria-hidden="true" />{cur.phone}</span>
                  )}
                  <button type="button" className="org-btn sm" onClick={() => props.onOpenFiche(cur.id)}>
                    Voir la fiche
                  </button>
                </div>
              </div>
              {peers.length > 0 && (
                <div className="fv-peers">
                  <span className="fv-peers-l">Au même niveau</span>
                  {peers.map((n) => (
                    <button
                      type="button"
                      key={n.id}
                      className={'fv-peer' + (dim(n) ? ' dim' : '')}
                      onClick={() => go(n.id)}
                      title={`${n.name} — ${n.role ?? 'Fonction non renseignée'}`}
                    >
                      <OrgAvatar n={n} s={26} color={entColor(entities, n.entityId)} />
                      {orgFirstName(n.name)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="fv-stem b" />

          <div className="fv-lvl">
            <span className="fv-lvl-l">Équipe</span>
            <div className="fv-lvl-c">
              {kids.length > 0 ? (
                <div className="fv-team">
                  <div className="fv-kids-h">
                    {kids.length} rapport{kids.length > 1 ? 's' : ''} direct{kids.length > 1 ? 's' : ''}
                    <em> · cliquez pour descendre · ↑↓←→ au clavier</em>
                  </div>
                  <div className="fv-kids">
                    {kids.map((n) => <MiniCard key={n.id} n={n} />)}
                  </div>
                  {canEdit && (
                    <button type="button" className="fv-addkid" onClick={() => setAdding(true)}>
                      <UserPlus size={13} aria-hidden="true" /> Ajouter des rapports directs
                    </button>
                  )}
                </div>
              ) : (
                <div className="fv-leaf">
                  Aucun rapport direct — fin de branche.
                  {canEdit && (
                    <button type="button" className="fv-addkid" onClick={() => setAdding(true)}>
                      <UserPlus size={13} aria-hidden="true" /> Ajouter des rapports directs
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {adding && (
        <AddReportsModal
          focus={cur}
          nodes={nodes}
          byId={byId}
          entities={entities}
          busy={busy}
          onClose={() => setAdding(false)}
          onAttach={(ids) => { props.onAttach(ids, cur.id); setAdding(false); }}
        />
      )}
    </div>
  );
}

// ─── manager select ──────────────────────────────────────────
// Descendants are excluded, so the server's cycle guard is never the
// first line of defence.
function ManagerPicker({
  node, nodes, byParent, busy, onSetManager,
}: {
  node: OrgNodeDTO;
  nodes: OrgNodeDTO[];
  byParent: Map<string, string[]>;
  busy: boolean;
  onSetManager: (id: string, managerId: string | null) => void;
}) {
  const desc = useMemo(() => orgDesc(node.id, byParent), [node.id, byParent]);
  const options = useMemo(
    () => nodes.filter((n) => n.id !== node.id && !desc.has(n.id)).sort((a, b) => a.name.localeCompare(b.name, 'fr')),
    [nodes, node.id, desc],
  );
  return (
    <label className="fv-mgrpick">
      <span>Rattacher à</span>
      <select
        value={node.managerId ?? ''}
        disabled={busy}
        onChange={(e) => onSetManager(node.id, e.target.value || null)}
        data-testid="org-manager-select"
      >
        <option value="">— Personne (sommet) —</option>
        {options.map((n) => (
          <option key={n.id} value={n.id}>{n.name}{n.role ? ` · ${n.role}` : ''}</option>
        ))}
      </select>
    </label>
  );
}

// ─── bulk attach ─────────────────────────────────────────────
// Building a 40-person chart one select at a time is painful, so the
// team block can adopt several people at once. Unattached people come
// first — that's the pile you work through on first use.
function AddReportsModal({
  focus, nodes, byId, entities, busy, onClose, onAttach,
}: {
  focus: OrgNodeDTO;
  nodes: OrgNodeDTO[];
  byId: Map<string, OrgNodeDTO>;
  entities: Map<string, EntityDTO>;
  busy: boolean;
  onClose: () => void;
  onAttach: (ids: string[]) => void;
}) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());

  const ancestors = useMemo(
    () => new Set(orgChain(focus.id, byId).map((n) => n.id)),
    [focus.id, byId],
  );
  const list = useMemo(
    () => nodes
      .filter((n) => n.id !== focus.id && n.managerId !== focus.id && !ancestors.has(n.id))
      .filter((n) => orgMatches(n, q))
      .sort((a, b) => Number(Boolean(a.managerId)) - Number(Boolean(b.managerId)) || a.name.localeCompare(b.name, 'fr')),
    [nodes, focus.id, ancestors, q],
  );

  const toggle = (id: string) =>
    setSel((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  return (
    <div className="org-ov" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="org-modal" role="dialog" aria-modal="true" aria-label="Ajouter des rapports directs">
        <div className="org-modal-h">
          <div>
            <h3>Rapports directs de {focus.name}</h3>
            <p>Les personnes cochées seront rattachées à {orgFirstName(focus.name)}.</p>
          </div>
          <button type="button" className="org-x" onClick={onClose} aria-label="Fermer">
            <X size={14} aria-hidden="true" />
          </button>
        </div>
        <div className="org-modal-s">
          <Search size={14} aria-hidden="true" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filtrer par nom ou fonction…"
            aria-label="Filtrer la liste"
          />
        </div>
        <div className="org-modal-list">
          {list.map((n) => {
            const color = entColor(entities, n.entityId);
            const mgr = n.managerId ? byId.get(n.managerId) : null;
            return (
              <label key={n.id} className={'org-pick' + (sel.has(n.id) ? ' on' : '')}>
                <input type="checkbox" checked={sel.has(n.id)} onChange={() => toggle(n.id)} />
                <OrgAvatar n={n} s={26} color={color} />
                <span className="org-pick-t">
                  <b>{n.name}</b>
                  <small>{n.role ?? 'Fonction non renseignée'}</small>
                </span>
                {mgr && <span className="org-pick-m">actuellement sous {orgFirstName(mgr.name)}</span>}
              </label>
            );
          })}
          {list.length === 0 && <p className="fv-rail-empty">Aucun candidat.</p>}
        </div>
        <div className="org-modal-f">
          <button type="button" className="org-btn ghost" onClick={onClose}>Annuler</button>
          <button
            type="button"
            className="org-btn"
            disabled={sel.size === 0 || busy}
            onClick={() => onAttach([...sel])}
            data-testid="org-attach-confirm"
          >
            Rattacher {sel.size > 0 ? `(${sel.size})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
